import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
  ArrowRight,
  Sparkles,
  CheckCircle2,
  Trophy,
  Flame,
  Video,
} from "lucide-react";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

/**
 * Hero — asymmetric split. Left: copy + CTA (staggered fade-up).
 * Right: floating product mockup with LIVE animations:
 *   • Task list — a task gets checked off every ~4s, rotating through the rows.
 *   • Chat bubble — reactions gently bounce in; "Katıl" button pulses.
 *   • XP badge — progress bar fills on mount, "+10 XP" sparkle pop every ~8s.
 *   • Whole card set staggers in with slight rotation.
 *   • Respects `prefers-reduced-motion` — falls back to a static frame.
 */
export function LandingHero() {
  const { t } = useTranslation();
  const reduced = useReducedMotion();

  const fadeUp = (delay = 0) =>
    reduced
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
        };

  return (
    <section className="serpix-hero-bg relative overflow-hidden">
      <div className="grain pointer-events-none absolute inset-0" />
      <motion.div
        className="pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full bg-violet-400/20 blur-3xl"
        animate={reduced ? undefined : { scale: [1, 1.08, 1] }}
        transition={reduced ? undefined : { duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="pointer-events-none absolute right-0 top-40 h-80 w-80 rounded-full bg-pink-400/20 blur-3xl"
        animate={reduced ? undefined : { scale: [1, 1.12, 1] }}
        transition={reduced ? undefined : { duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 pb-24 pt-14 md:grid-cols-12 md:pb-32 md:pt-24 lg:px-8">
        {/* Left copy */}
        <div className="md:col-span-6 lg:col-span-7">
          <motion.div
            {...fadeUp(0)}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-violet-200 bg-white/70 px-3 py-1 text-xs font-medium text-violet-700 shadow-sm backdrop-blur"
            data-testid="landing-hero-badge"
          >
            <Sparkles className="h-3 w-3" />
            {t("landing.hero.badge")}
          </motion.div>

          <motion.h1
            {...fadeUp(0.1)}
            className="font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-zinc-950 sm:text-5xl md:text-6xl lg:text-7xl"
            data-testid="landing-hero-title"
          >
            {t("landing.hero.h1_prefix")}
            <span className="serpix-gradient-text">{t("landing.hero.h1_gradient")}</span>
            {t("landing.hero.h1_suffix")}
          </motion.h1>

          <motion.p
            {...fadeUp(0.2)}
            className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600 md:text-lg"
          >
            {t("landing.hero.subtitle")}
          </motion.p>

          <motion.div
            {...fadeUp(0.3)}
            className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center"
          >
            <Link to="/register">
              <Button
                size="lg"
                className="gap-2 bg-gradient-to-r from-[#5B4CFB] to-[#7c5cf6] text-white shadow-xl shadow-violet-500/30 hover:from-[#4f40f0] hover:to-[#6f4df0]"
                data-testid="landing-hero-cta-primary"
              >
                {t("landing.hero.cta_primary")}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <a href="#pricing">
              <Button
                size="lg"
                variant="outline"
                className="border-zinc-300 bg-white/70 text-zinc-900 backdrop-blur hover:bg-white"
                data-testid="landing-hero-cta-secondary"
              >
                {t("landing.hero.cta_secondary")}
              </Button>
            </a>
          </motion.div>

          <motion.p {...fadeUp(0.4)} className="mt-6 text-xs text-zinc-500">
            {t("landing.hero.trust")}
          </motion.p>

          <motion.p
            {...fadeUp(0.45)}
            className="mt-4 text-sm text-zinc-600"
            data-testid="landing-hero-login-prompt"
          >
            {t("landing.hero.have_account")}{" "}
            <Link
              to="/login"
              data-testid="landing-hero-login-link"
              className="font-semibold text-violet-700 underline underline-offset-4 decoration-violet-300 transition-colors hover:text-violet-800 hover:decoration-violet-500"
            >
              {t("landing.hero.login_link")} →
            </Link>
          </motion.p>
        </div>

        {/* Right — animated mockup */}
        <div className="md:col-span-6 lg:col-span-5">
          <HeroMockup reduced={reduced} />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── HERO MOCKUP ─────────────────── */

const TASK_ROWS = [
  { id: 0, label: "Yeni landing hero'yu tasarla", tag: "high" },
  { id: 1, label: "Kanban görünümünü toparla", tag: "med" },
  { id: 2, label: "Motivasyon rozetleri", tag: "low" },
  { id: 3, label: "Faturalama akışını düzenle", tag: "urg" },
];

function HeroMockup({ reduced }) {
  // Rotating "done" index for the task list. Persist doneSet so completed tasks
  // stay checked until a full cycle resets.
  const [doneSet, setDoneSet] = useState(new Set([0])); // start with first done
  const [xp, setXp] = useState(1240);
  const [xpBurst, setXpBurst] = useState(0); // increments to trigger the sparkle key

  useEffect(() => {
    if (reduced) return;
    let idx = 1;
    const tick = setInterval(() => {
      setDoneSet((prev) => {
        // Reset the cycle after all four are done.
        if (prev.size >= TASK_ROWS.length) {
          idx = 0;
          return new Set([0]);
        }
        const next = new Set(prev);
        next.add(idx);
        idx += 1;
        return next;
      });
    }, 3200);
    return () => clearInterval(tick);
  }, [reduced]);

  useEffect(() => {
    if (reduced) return;
    const bump = setInterval(() => {
      setXp((v) => v + 10);
      setXpBurst((b) => b + 1);
    }, 6500);
    return () => clearInterval(bump);
  }, [reduced]);

  const cardEnter = (delay) =>
    reduced
      ? {}
      : {
          initial: { opacity: 0, y: 40, rotate: 0 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
        };

  const xpProgress = Math.min(1, (xp - 1000) / 500); // full at 1500

  return (
    <div className="relative h-[440px] w-full md:h-[520px]">
      {/* Main workspace card */}
      <motion.div
        {...cardEnter(0.15)}
        whileHover={reduced ? undefined : { y: -4 }}
        className="absolute inset-x-0 top-0 z-10 rotate-[-1.5deg] rounded-2xl border border-zinc-200 bg-white shadow-2xl shadow-violet-500/15"
      >
        <div className="flex items-center gap-2 border-b border-zinc-100 px-4 py-2.5">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          </div>
          <span className="ml-3 text-[11px] font-medium text-zinc-500">
            serpix.co / workspace
          </span>
        </div>
        <div className="grid grid-cols-[120px_1fr] text-xs">
          {/* Sidebar */}
          <div className="border-r border-zinc-100 bg-zinc-50/60 p-3 space-y-1.5">
            {["Dashboard", "Projeler", "Takvim", "Motivasyon", "Hızlı Not"].map((l, i) => (
              <div
                key={l}
                className={
                  i === 1
                    ? "rounded-lg bg-violet-100 px-2 py-1.5 font-semibold text-violet-700"
                    : "rounded-lg px-2 py-1.5 text-zinc-500"
                }
              >
                {l}
              </div>
            ))}
            <div className="mt-4 border-t border-zinc-200 pt-3 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
              Kanallar
            </div>
            {["# website", "# marketing", "# ceo"].map((c) => (
              <div key={c} className="rounded-lg px-2 py-1 text-zinc-500">
                {c}
              </div>
            ))}
          </div>
          {/* Task list */}
          <div className="space-y-2 p-3">
            <div className="mb-1 flex items-center justify-between">
              <div className="text-sm font-semibold text-zinc-900">Website Redesign</div>
              <motion.div
                key={`open-${doneSet.size}`}
                initial={reduced ? false : { scale: 0.85, opacity: 0.6 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.25 }}
                className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"
              >
                {Math.max(0, 18 - doneSet.size)} açık
              </motion.div>
            </div>
            {TASK_ROWS.map((row, i) => {
              const done = doneSet.has(row.id);
              return (
                <motion.div
                  key={row.id}
                  {...cardEnter(0.3 + i * 0.08)}
                  className="relative flex items-center gap-2 overflow-hidden rounded-lg border border-zinc-100 bg-white px-2.5 py-2"
                >
                  <motion.span
                    animate={done ? { scale: [1, 1.35, 1] } : { scale: 1 }}
                    transition={{ duration: 0.4 }}
                  >
                    <CheckCircle2
                      className={
                        "h-3.5 w-3.5 " + (done ? "text-violet-500" : "text-zinc-300")
                      }
                      strokeWidth={done ? 2.5 : 2}
                    />
                  </motion.span>
                  <span
                    className={
                      "relative flex-1 truncate text-[11px] transition-colors " +
                      (done ? "text-zinc-400" : "text-zinc-800")
                    }
                  >
                    {row.label}
                    {/* Strike-through line drawn via a growing pseudo-element */}
                    <motion.span
                      aria-hidden="true"
                      initial={false}
                      animate={{ scaleX: done ? 1 : 0 }}
                      transition={{ duration: 0.45, ease: "easeOut" }}
                      className="pointer-events-none absolute left-0 top-1/2 h-px w-full origin-left bg-zinc-400"
                    />
                  </span>
                  <TagChip tag={row.tag} />
                </motion.div>
              );
            })}
            <div className="pt-2 text-[10px] text-zinc-400">
              ↑↓ gezin · Enter aç · Space tamamla
            </div>
          </div>
        </div>
      </motion.div>

      {/* Floating chat bubble */}
      <motion.div
        {...cardEnter(0.6)}
        whileHover={reduced ? undefined : { y: -4, rotate: 0 }}
        className="absolute -bottom-4 left-2 z-20 w-[62%] rotate-[1.5deg] rounded-2xl border border-zinc-200 bg-white p-3 shadow-xl shadow-violet-500/15"
        data-testid="landing-hero-mockup-chat"
      >
        <div className="mb-2 flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-100 text-[11px] font-bold text-violet-700">
            EK
          </div>
          <div className="text-[11px] font-semibold text-zinc-900">Elif Kaya</div>
          <div className="text-[10px] text-zinc-400">şimdi</div>
          <motion.div
            animate={reduced ? undefined : { scale: [1, 1.06, 1] }}
            transition={reduced ? undefined : { duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="ml-auto flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"
          >
            <Video className="h-3 w-3" /> Katıl
          </motion.div>
        </div>
        <div className="rounded-xl bg-violet-50 px-3 py-2 text-[11px] text-zinc-800">
          Toplantı 14:00&apos;te başlıyor, herkese görev düşecek 🚀
        </div>
        <div className="mt-2 flex items-center gap-1.5">
          <BounceChip e="👍" c="3" delay={0.9} reduced={reduced} />
          <BounceChip e="🎉" c="2" delay={1.15} reduced={reduced} />
          <BounceChip e="🔥" c="1" delay={1.4} reduced={reduced} />
        </div>
      </motion.div>

      {/* Floating XP badge */}
      <motion.div
        {...cardEnter(0.45)}
        whileHover={reduced ? undefined : { y: -4, rotate: 3.5 }}
        className="absolute -right-2 bottom-16 z-30 w-[46%] rotate-[3deg] rounded-2xl border border-zinc-200 bg-gradient-to-br from-white via-violet-50 to-pink-50 p-3 shadow-xl shadow-pink-500/20"
        data-testid="landing-hero-mockup-xp"
      >
        <div className="flex items-center gap-2">
          <motion.div
            animate={reduced ? undefined : { rotate: [0, -6, 6, -6, 0] }}
            transition={reduced ? undefined : { duration: 1.4, repeat: Infinity, repeatDelay: 3.5, ease: "easeInOut" }}
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-amber-600"
          >
            <Trophy className="h-4 w-4" />
          </motion.div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Seviye 7</div>
            <motion.div
              key={xp}
              initial={reduced ? false : { scale: 1.2, color: "#7c3aed" }}
              animate={{ scale: 1, color: "#18181b" }}
              transition={{ duration: 0.5 }}
              className="text-sm font-bold"
            >
              {xp.toLocaleString("tr-TR")} XP
            </motion.div>
          </div>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-zinc-200">
          <motion.div
            initial={reduced ? false : { width: "0%" }}
            animate={{ width: `${xpProgress * 100}%` }}
            transition={{ duration: 1.2, ease: "easeOut", delay: reduced ? 0 : 0.9 }}
            className="h-full rounded-full bg-gradient-to-r from-violet-500 to-pink-500"
          />
        </div>
        <div className="mt-2 flex items-center gap-1 text-[10px] font-semibold text-orange-600">
          <motion.span
            animate={reduced ? undefined : { scale: [1, 1.2, 1] }}
            transition={reduced ? undefined : { duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            className="inline-flex"
          >
            <Flame className="h-3 w-3" />
          </motion.span>
          12 günlük seri
        </div>

        {/* Sparkle burst on XP bump */}
        <AnimatePresence>
          {!reduced && xpBurst > 0 && (
            <motion.div
              key={xpBurst}
              initial={{ opacity: 0, y: 8, scale: 0.8 }}
              animate={{ opacity: 1, y: -14, scale: 1 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 1.6, ease: "easeOut" }}
              className="pointer-events-none absolute -top-1 right-3 flex items-center gap-1 rounded-full bg-gradient-to-r from-violet-500 to-pink-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-lg shadow-pink-500/30"
            >
              <Sparkles className="h-3 w-3" /> +10 XP
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Push notification bubble — slides in from top-right to highlight the
          new Web Push feature. Sits above the workspace card, styled like
          an iOS lock-screen alert. */}
      <motion.div
        initial={reduced ? false : { opacity: 0, x: 40, y: -20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.7, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className="absolute right-2 top-2 z-30 w-[220px] rounded-xl border border-zinc-200 bg-white/90 p-2.5 shadow-lg shadow-violet-500/20 backdrop-blur"
        data-testid="hero-push-bubble"
      >
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-[5px] bg-violet-600 text-[8px] font-bold text-white">
              S
            </span>
            <span className="text-[9px] font-semibold uppercase tracking-wide text-zinc-500">
              Serpix
            </span>
          </div>
          <span className="text-[9px] text-zinc-400">şimdi</span>
        </div>
        <div className="text-[11px] font-semibold text-zinc-900">Elif Kaya</div>
        <div className="line-clamp-2 text-[10px] text-zinc-600">
          Toplantı 14:00&apos;te başlıyor, herkese görev düşecek 🚀
        </div>
      </motion.div>
    </div>
  );
}

function TagChip({ tag }) {
  const map = {
    urg: "bg-red-100 text-red-700",
    high: "bg-amber-100 text-amber-700",
    med: "bg-sky-100 text-sky-700",
    low: "bg-zinc-100 text-zinc-500",
  };
  const label = { urg: "Acil", high: "Yüksek", med: "Orta", low: "Düşük" }[tag];
  return (
    <span
      className={
        "rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase " + map[tag]
      }
    >
      {label}
    </span>
  );
}

function BounceChip({ e, c, delay, reduced }) {
  return (
    <motion.span
      initial={reduced ? false : { scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{
        delay: reduced ? 0 : delay,
        type: reduced ? "tween" : "spring",
        stiffness: 400,
        damping: 12,
      }}
      className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-1.5 py-0.5 text-[10px]"
    >
      <span>{e}</span>
      <span className="font-semibold text-zinc-600">{c}</span>
    </motion.span>
  );
}
